import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Radio,
  Lock,
  Unlock,
  Trash2,
  MonitorPlay,
  Network,
  MessageSquare,
  Activity,
  Boxes,
  Gauge,
  Users,
} from 'lucide-react';
import {
  useRelaySessions,
  useRelayMetrics,
  useCreateRelaySession,
  useDeleteRelaySession,
} from '../hooks/useRelay';
import { RELAY_KIND_LABELS, type RelaySession, type RelaySessionKind } from '../types/relay';
import StatCard from '../components/StatCard';

function kindIcon(kind: RelaySessionKind) {
  switch (kind) {
    case 'instance':
      return MonitorPlay;
    case 'peer-runner':
      return Network;
    default:
      return MessageSquare;
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}

function formatDuration(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.round(hr / 24)}d`;
}

function sessionLabel(session: RelaySession): string {
  if (session.tag) return session.tag;
  return session.id.slice(0, 8);
}

function LiveStatus({ session }: { session: RelaySession }) {
  if (session.liveConnections > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
        Connected · {session.liveConnections}
      </span>
    );
  }
  if (session.active) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
        Idle
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
      Offline
    </span>
  );
}

function EncryptionBadge({ encrypted }: { encrypted: boolean }) {
  if (encrypted) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-emerald-600"
        title="Server holds a data encryption key; stored content is ciphertext at rest."
      >
        <Lock className="h-3.5 w-3.5" />
        Encrypted at rest
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-amber-600"
      title="No server-side data encryption key was established for this session."
    >
      <Unlock className="h-3.5 w-3.5" />
      No server key
    </span>
  );
}

type Filter = 'all' | 'active' | 'idle';

export default function RelaySessions() {
  const { data: sessions, isLoading } = useRelaySessions();
  const { data: metrics } = useRelayMetrics();
  const createSession = useCreateRelaySession();
  const deleteSession = useDeleteRelaySession();
  const [filter, setFilter] = useState<Filter>('all');

  const list = sessions ?? [];
  const filtered = list.filter((s) => {
    if (filter === 'active') return s.liveConnections > 0;
    if (filter === 'idle') return s.liveConnections === 0;
    return true;
  });

  const filters: { key: Filter; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: list.length },
    { key: 'active', label: 'Connected', count: list.filter((s) => s.liveConnections > 0).length },
    { key: 'idle', label: 'Idle', count: list.filter((s) => s.liveConnections === 0).length },
  ];

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Relay Sessions</h1>
          <p className="mt-1 text-sm text-slate-500">
            End-to-end encrypted Socket.IO tunnels brokered between peers. The server relays
            ciphertext only.
          </p>
        </div>
        <button
          onClick={() => createSession.mutate({ tag: 'Manual session' })}
          disabled={createSession.isPending}
          className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          New Session
        </button>
      </div>

      {/* Metrics strip */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Boxes} label="Active sessions" value={metrics?.activeSessions ?? '—'} />
        <StatCard icon={Activity} label="Total events" value={metrics?.totalMessages ?? '—'} />
        <StatCard
          icon={Gauge}
          label="Avg session age"
          value={metrics ? formatDuration(metrics.avgSessionAgeMs) : '—'}
        />
      </div>

      {/* Filter tabs */}
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              filter === f.key
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {f.label}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{f.count}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading sessions...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <Radio className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            {list.length === 0 ? 'No relay sessions yet.' : 'No sessions match this filter.'}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Relay sessions are created automatically for instance and peer-runner tunnels, or
            manually here.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Session</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Encryption</th>
                <th className="px-4 py-3 font-medium">Events</th>
                <th className="px-4 py-3 font-medium">Last active</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((session) => {
                const Icon = kindIcon(session.kind);
                return (
                  <tr key={session.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100">
                          <Icon className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                          <Link
                            to={`/relay/${session.id}`}
                            className="font-medium text-slate-900 hover:text-blue-600"
                          >
                            {sessionLabel(session)}
                          </Link>
                          <div className="text-xs text-slate-400">
                            {RELAY_KIND_LABELS[session.kind]} · {session.id.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <LiveStatus session={session} />
                    </td>
                    <td className="px-4 py-3">
                      <EncryptionBadge encrypted={session.encrypted} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">{session.seq}</td>
                    <td className="px-4 py-3 text-slate-500">{relativeTime(session.lastActiveAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              'Delete this relay session? This permanently removes all stored messages.',
                            )
                          ) {
                            deleteSession.mutate(session.id);
                          }
                        }}
                        className="text-slate-400 hover:text-red-500"
                        title="Delete session"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {list.some((s) => s.kind === 'peer-runner' || s.kind === 'instance') && (
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-400">
          <Users className="h-3.5 w-3.5" />
          Instance and peer-runner sessions are machine tunnels — their detail view is read-only.
        </p>
      )}
    </div>
  );
}
